import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams, Prompt } from 'react-router-dom';
import * as R from 'ramda';
import { routerWillLeave } from '@actions/confirmation-actions';
import GroupsDateRangeDialog from '@components/group/dialogs/date-range';
import {
  changeGroupFieldValue,
  createGroupWithEntities,
  createNewGroup,
  getGroupById,
  updateGroup
} from '@actions/groups-actions';
import { getGroupDetailsConfig } from '@constants/config';
import FormElement from '@forms/form-element';
import Button from '@material-ui/core/Button';
import { getAdminAgencyAdminAndManagerUsers, getAdminAgencyUsers } from '@selectors/data-table-selector';
import { getGroupIsLoading } from '@selectors/groups-selector';
import DotmapsLoader from '@shared/dotmaps-loader';
import FlexSeparator from '@shared/helpers/flex-separator';
import {
  applyDynamicStyles,
  getAction,
  mergeActionWithTemplate
} from '@utils/data-detail-utils';
import { prepareGroupData } from '@utils/group-utils';
import { canEditGroups } from '@utils/permission-utils';
import { sortBy } from '@utils/shared-utils';
import BoundaryRow from './boundary-row';
import DateRow from './date-row';
import '../drawer.scss';

import InputSearch from '@shared/input-search';

const Details = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const prevLocationRef = useRef(location);

  // Redux state
  const edit = useSelector(state => state.groups.edit);
  const options = useSelector(state => state.groups.options);
  const discard = useSelector(state => state.confirmation.discard);
  const user = useSelector(state => sortBy(R.values(getAdminAgencyAdminAndManagerUsers(state)), 'name'));
  const adminAgencyUsers = useSelector(getAdminAgencyUsers);
  const loading = useSelector(getGroupIsLoading);
  const groupStatuses = useSelector(state => state.dataTypes.group_status);

  // Destructure edit state
  const { group, modified, error: errors, saving, drawing } = edit;

  const { id, groupType } = params;

  const initializePage = useCallback(({ skipNewGroup = false } = {}) => {
    if (id !== 'new' && !!+id) {
      dispatch(getGroupById(id));
    }
    if (id === 'new' && !skipNewGroup) {
      dispatch(createNewGroup(groupType));
    }
    const entityIds = R.path(['state', 'entityIds'], location);
    if (entityIds) {
      const includeOverlaps = R.path(['state', 'includeOverlaps'], location);
      dispatch(createGroupWithEntities(entityIds, includeOverlaps));
    }
  }, [id, groupType, location, dispatch]);

  // Initial load on mount
  useEffect(() => {
    // If the group already has a status, it was previously initialized
    // (e.g. this is a remount after switching tabs). Skip createNewGroup
    // to avoid clearing user-entered data.
    const alreadyInitialized = id === 'new' && group && group.status;
    initializePage({ skipNewGroup: alreadyInitialized });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-initialize when location changes
  useEffect(() => {
    if (location !== prevLocationRef.current) {
      initializePage();
    }
    prevLocationRef.current = location;
  }, [location, initializePage]);

  const onChange = useCallback((field, value) => {
    dispatch(changeGroupFieldValue(field, value));
  }, [dispatch]);

  const clickSave = useCallback(() => {
    const source = location.state ? location.state.source : null;
    dispatch(updateGroup(prepareGroupData(edit), groupType, source));
  }, [dispatch, edit, groupType, location.state]);

  const getConfig = useCallback(() => {
    return getGroupDetailsConfig(groupType);
  }, [groupType]);

  const isBoundary = () => getConfig().form.boundary;

  const getFormLegend = () => getConfig().form.legend;

  const getDetailsTab = () => getConfig().tabs[0];

  const getMetadata = useCallback(() => {
    // Get backend fields definition
    // (i.e. field name, type, label, etc).
    const { action } = getAction(options);
    // Get frontend field definition
    // (i.e. field name, CSS style, etc).
    const fieldTemplate = getConfig().form;
    // And merge both:
    return applyDynamicStyles(mergeActionWithTemplate(action || {}, fieldTemplate), group || {});
  }, [options, group, getConfig]);

  const getDataSource = useCallback(field => {
    if (field === 'status') {
      return groupStatuses;
    }
    if (field === 'owners') {
      return R.values(user);
    }
    return null;
  }, [groupStatuses, user]);

  const getFieldValue = useCallback(field => {
    if (field === 'owners' && group[field]) {
      // The API returns owners as objects ({id, name, ...}), but
      // InputSearch expects an array of IDs to match against options.
      return group[field].map(owner => typeof owner === 'object' ? owner.id : owner);
    }
    return group[field] || '';
  }, [group]);

  if (loading || ((!group || !group.id) && id !== 'new')) {
    return (
      <div styleName="group-drawer-tab-loader-container">
        <DotmapsLoader />
      </div>
    );
  }

  const isReadOnly = !canEditGroups(groupType);
  const detailsTab = getDetailsTab();
  const metadata = getMetadata();
  const boundary = isBoundary();
  const legend = getFormLegend();

  return (
    <div styleName="group-drawer-tab-content">
      <div styleName="container">
        <div styleName="header">
          <div styleName="title">{detailsTab.label}</div>
          {detailsTab.extra && (
            <div styleName="extra">
              {detailsTab.extra}
            </div>
          )}
          <FlexSeparator />
          {saving &&
            <div styleName="loader"><DotmapsLoader small /></div>
          }
          {!isReadOnly && (
            <Button
              color="primary"
              disabled={saving || drawing}
              onClick={clickSave}
              variant="contained"
            >
              SAVE
            </Button>
          )}
        </div>
        <div styleName="form-legend-container">
          {legend && (
            <div styleName="form-legend-text">
              {legend}
            </div>
          )}
        </div>
        <div styleName="form-row">
          {detailsTab.fields.map(field => {
            if (field === 'owners') {
              const filterFunc = item => {
                return Object.keys(adminAgencyUsers).indexOf(String(item.id)) > -1;
              };
              return (
                <InputSearch
                  autocompleteContainerStyles={{ maxWidth: 'none' }}
                  containerStyles={{ margin: '0' }}
                  dataType="user"
                  errors={errors.owners || null}
                  fieldName={field}
                  filterFunc={filterFunc}
                  onChange={(event, newValues) => {
                    if (Array.isArray(newValues)) {
                      const values = newValues.map(value => value.value);
                      onChange('owners', values);
                    } else {
                      onChange('owners', [newValues.value]);
                    }
                  }}
                  label={'Owners'}
                  storePath={['dataTypes', 'user']}
                  values={getFieldValue('owners')}
                  key={field}
                />
              );
            }
            return (
              <FormElement
                data={group}
                dataSource={getDataSource(field)}
                errors={errors}
                key={field}
                fieldMeta={metadata[field]}
                fieldName={field}
                onChange={onChange}
                readOnly={isReadOnly}
                value={getFieldValue(field)}
              />
            );
          })}
        </div>
      </div>
      {boundary && (
        <div styleName="container">
          <div styleName="title">Boundary and date range</div>
          <div styleName="description">
            A boundary group helps to facilitate coordination, it contains records that fall within a defined area and a date range.
            Records will be automatically loaded after group creation. You can also manually add records.
          </div>
          <BoundaryRow />
          <DateRow />
        </div>
      )}
      {boundary && <GroupsDateRangeDialog />}
      <Prompt when={modified && !discard} message={routerWillLeave} />
    </div>
  );
};

export default Details;
